import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  X, 
  Clock, 
  MapPin, 
  Users, 
  Bell, 
  Trash2, 
  Edit2, 
  Send,
  Folder,
  Wrench
} from 'lucide-react';
import { getBackendUrl } from '../utils/getBackendUrl';
import { toETInputValue } from '../utils/timezone';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const EVENT_TYPES = [
  { value: 'meeting', label: 'Meeting', color: '#3b82f6' },
  { value: 'project', label: 'Project', color: '#10b981' },
  { value: 'service', label: 'Service', color: '#8b5cf6' },
  { value: 'reminder', label: 'Reminder', color: '#f59e0b' },
  { value: 'holiday', label: 'Holiday Request', color: '#ea580c' },
  { value: 'time_off', label: 'Time Off Request', color: '#7c3aed' },
  { value: 'other', label: 'Other', color: '#6b7280' }
];

const REMINDER_OPTIONS = [
  { value: 15, unit: 'minutes', label: '15 minutes before' },
  { value: 30, unit: 'minutes', label: '30 minutes before' },
  { value: 1, unit: 'hours', label: '1 hour before' },
  { value: 2, unit: 'hours', label: '2 hours before' },
  { value: 24, unit: 'hours', label: '24 hours before' },
  { value: 1, unit: 'days', label: '1 day before' },
  { value: 2, unit: 'days', label: '2 days before' },
  { value: 7, unit: 'days', label: '1 week before' }
];

const Calendar = ({ user }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Form state for creating/editing events
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_type: 'meeting',
    start_datetime: '',
    end_datetime: '',
    all_day: false,
    project_id: '',
    service_id: '',
    attendees: [],
    reminders: [],
    color: '#3b82f6',
    location: '',
    notes: ''
  });

  const fetchEvents = useCallback(async () => {
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      // Extend range for week view
      const start = new Date(startOfMonth);
      start.setDate(start.getDate() - 7);
      const end = new Date(endOfMonth);
      end.setDate(end.getDate() + 7);
      
      const response = await axios.get(`${API}/calendar/events`, {
        params: {
          start_date: start.toISOString(),
          end_date: end.toISOString()
        }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchProjects();
    fetchServices();
    fetchUsers();
  }, [fetchEvents]);

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        ...eventForm,
        start_datetime: new Date(eventForm.start_datetime).toISOString(),
        end_datetime: eventForm.end_datetime ? new Date(eventForm.end_datetime).toISOString() : null
      };
      
      await axios.post(`${API}/calendar/events`, eventData);
      setShowCreateModal(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event');
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        ...eventForm,
        start_datetime: new Date(eventForm.start_datetime).toISOString(),
        end_datetime: eventForm.end_datetime ? new Date(eventForm.end_datetime).toISOString() : null
      };
      
      await axios.put(`${API}/calendar/events/${selectedEvent.id}`, eventData);
      setShowEventModal(false);
      setSelectedEvent(null);
      resetForm();
      fetchEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Failed to update event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(`${API}/calendar/events/${eventId}`);
      setShowEventModal(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event');
    }
  };

  const handleSendReminders = async (eventId) => {
    try {
      const response = await axios.post(`${API}/calendar/events/${eventId}/send-reminders`);
      alert(response.data.message);
    } catch (error) {
      console.error('Error sending reminders:', error);
      alert('Failed to send reminders');
    }
  };

  const resetForm = () => {
    setEventForm({
      title: '',
      description: '',
      event_type: 'meeting',
      start_datetime: '',
      end_datetime: '',
      all_day: false,
      project_id: '',
      service_id: '',
      attendees: [],
      reminders: [],
      color: '#3b82f6',
      location: '',
      notes: ''
    });
  };

  const openCreateModal = (date = null) => {
    resetForm();
    if (date) {
      const dateStr = toETInputValue(date);
      setEventForm(prev => ({ ...prev, start_datetime: dateStr }));
    }
    setShowCreateModal(true);
  };

  const openEventModal = (event) => {
    setSelectedEvent(event);
    setEventForm({
      title: event.title,
      description: event.description || '',
      event_type: event.event_type,
      start_datetime: event.start_datetime.slice(0, 16),
      end_datetime: event.end_datetime ? event.end_datetime.slice(0, 16) : '',
      all_day: event.all_day,
      project_id: event.project_id || '',
      service_id: event.service_id || '',
      attendees: event.attendees || [],
      reminders: event.reminders || [],
      color: event.color || '#3b82f6',
      location: event.location || '',
      notes: event.notes || ''
    });
    setShowEventModal(true);
  };

  const addReminder = () => {
    setEventForm(prev => ({
      ...prev,
      reminders: [...prev.reminders, { time_before: 1, unit: 'hours', email_sent: false }]
    }));
  };

  const removeReminder = (index) => {
    setEventForm(prev => ({
      ...prev,
      reminders: prev.reminders.filter((_, i) => i !== index)
    }));
  };

  const updateReminder = (index, field, value) => {
    setEventForm(prev => ({
      ...prev,
      reminders: prev.reminders.map((r, i) => i === index ? { ...r, [field]: value } : r)
    }));
  };

  // Calendar rendering helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    const prevMonth = new Date(year, month, 0);
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  const getWeekDays = (date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push({ date: day, isCurrentMonth: true });
    }
    return days;
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start_datetime);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const navigateWeek = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + (direction * 7));
      return newDate;
    });
  };

  const getEventColor = (eventType) => {
    const type = EVENT_TYPES.find(t => t.value === eventType);
    return type ? type.color : '#6b7280';
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto', hour: '2-digit', minute: '2-digit' });
  };

  const days = viewMode === 'month' ? getDaysInMonth(currentDate) : getWeekDays(currentDate);
  const isToday = (date) => date.toDateString() === new Date().toDateString();

  const canEdit = (event) => {
    return user.role === 'admin' || user.role === 'supervisor' || event.created_by === user.id;
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <CalendarIcon className="w-8 h-8 text-red-600" />
              Calendar
            </h1>
            <p className="text-gray-600 mt-1">Manage events, meetings, and reminders</p>
          </div>
          <button
            onClick={() => openCreateModal()}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            New Event
          </button>
        </div>

        {/* Calendar Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => viewMode === 'month' ? navigateMonth(-1) : navigateWeek(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-semibold text-gray-900 min-w-[200px] text-center">
                {currentDate.toLocaleDateString('en-US', { timeZone: 'America/Toronto', month: 'long', 
                  year: 'numeric',
                  ...(viewMode === 'week' && { day: 'numeric' })
                })}
              </h2>
              <button
                onClick={() => viewMode === 'month' ? navigateMonth(1) : navigateWeek(1)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Today
              </button>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-4 py-2 text-sm ${viewMode === 'month' ? 'bg-red-600 text-white' : 'bg-white hover:bg-gray-50'}`}
                >
                  Month
                </button>
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-4 py-2 text-sm ${viewMode === 'week' ? 'bg-red-600 text-white' : 'bg-white hover:bg-gray-50'}`}
                >
                  Week
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="p-3 text-center text-sm font-semibold text-gray-600 bg-gray-50">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className={`grid grid-cols-7 ${viewMode === 'month' ? '' : 'min-h-[400px]'}`}>
            {days.map((day, index) => {
              const dayEvents = getEventsForDate(day.date);
              return (
                <div
                  key={index}
                  className={`min-h-[100px] border-b border-r border-gray-200 p-2 cursor-pointer hover:bg-gray-50 transition-colors ${
                    !day.isCurrentMonth ? 'bg-gray-50' : ''
                  } ${isToday(day.date) ? 'bg-red-50' : ''}`}
                  onClick={() => openCreateModal(day.date)}
                >
                  <div className={`text-sm font-medium mb-1 ${
                    isToday(day.date) ? 'text-red-600' : day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {day.date.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map(event => (
                      <div
                        key={event.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEventModal(event);
                        }}
                        className="text-xs p-1 rounded truncate cursor-pointer hover:opacity-80"
                        style={{ backgroundColor: event.color || getEventColor(event.event_type), color: 'white' }}
                      >
                        {event.all_day ? event.title : `${formatTime(event.start_datetime)} ${event.title}`}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-xs text-gray-500">+{dayEvents.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Event Type Legend */}
        <div className="mt-4 flex flex-wrap gap-4">
          {EVENT_TYPES.map(type => (
            <div key={type.value} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }} />
              <span className="text-sm text-gray-600">{type.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Create Event Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">Create New Event</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateEvent} className="p-6 space-y-4">
              <EventFormFields 
                eventForm={eventForm}
                setEventForm={setEventForm}
                projects={projects}
                services={services}
                users={users}
                addReminder={addReminder}
                removeReminder={removeReminder}
                updateReminder={updateReminder}
              />
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Create Event
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View/Edit Event Modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">Event Details</h2>
              <button onClick={() => setShowEventModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleUpdateEvent} className="p-6 space-y-4">
              <EventFormFields 
                eventForm={eventForm}
                setEventForm={setEventForm}
                projects={projects}
                services={services}
                users={users}
                addReminder={addReminder}
                removeReminder={removeReminder}
                updateReminder={updateReminder}
                readOnly={!canEdit(selectedEvent)}
              />
              
              {/* Event Info */}
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                <p>Created by: {selectedEvent.created_by_name}</p>
                <p>Created: {new Date(selectedEvent.created_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}</p>
                {selectedEvent.updated_at && (
                  <p>Last updated: {new Date(selectedEvent.updated_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}</p>
                )}
              </div>
              
              <div className="flex flex-wrap gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEventModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                {(user.role === 'admin' || user.role === 'supervisor') && (
                  <button
                    type="button"
                    onClick={() => handleSendReminders(selectedEvent.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Send className="w-4 h-4" />
                    Send Reminders
                  </button>
                )}
                {canEdit(selectedEvent) && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleDeleteEvent(selectedEvent.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                    <button
                      type="submit"
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      <Edit2 className="w-4 h-4" />
                      Save Changes
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Separate component for form fields
const EventFormFields = ({ 
  eventForm, 
  setEventForm, 
  projects, 
  services, 
  users,
  addReminder,
  removeReminder,
  updateReminder,
  readOnly = false
}) => {
  return (
    <>
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
        <input
          type="text"
          value={eventForm.title}
          onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
          required
          disabled={readOnly}
        />
      </div>

      {/* Event Type & Color */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
          <select
            value={eventForm.event_type}
            onChange={(e) => {
              const type = EVENT_TYPES.find(t => t.value === e.target.value);
              setEventForm(prev => ({ 
                ...prev, 
                event_type: e.target.value,
                color: type?.color || prev.color
              }));
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
            disabled={readOnly}
          >
            {EVENT_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
          <input
            type="color"
            value={eventForm.color}
            onChange={(e) => setEventForm(prev => ({ ...prev, color: e.target.value }))}
            className="w-full h-10 rounded-lg cursor-pointer"
            disabled={readOnly}
          />
        </div>
      </div>

      {/* Date & Time */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date/Time *</label>
          <input
            type="datetime-local"
            value={eventForm.start_datetime}
            onChange={(e) => setEventForm(prev => ({ ...prev, start_datetime: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
            required
            disabled={readOnly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date/Time</label>
          <input
            type="datetime-local"
            value={eventForm.end_datetime}
            onChange={(e) => setEventForm(prev => ({ ...prev, end_datetime: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
            disabled={readOnly}
          />
        </div>
      </div>

      {/* All Day Toggle */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={eventForm.all_day}
          onChange={(e) => setEventForm(prev => ({ ...prev, all_day: e.target.checked }))}
          className="w-4 h-4 text-red-600 rounded"
          disabled={readOnly}
        />
        <span className="text-sm text-gray-700">All day event</span>
      </label>

      {/* Link to Project */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Folder className="w-4 h-4 inline mr-1" />
          Link to Project
        </label>
        <select
          value={eventForm.project_id}
          onChange={(e) => setEventForm(prev => ({ ...prev, project_id: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          disabled={readOnly}
        >
          <option value="">No project linked</option>
          {projects.map(project => (
            <option key={project.id} value={project.id}>{project.name}</option>
          ))}
        </select>
      </div>

      {/* Link to Service */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Wrench className="w-4 h-4 inline mr-1" />
          Link to Service
        </label>
        <select
          value={eventForm.service_id}
          onChange={(e) => setEventForm(prev => ({ ...prev, service_id: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          disabled={readOnly}
        >
          <option value="">No service linked</option>
          {services.map(service => (
            <option key={service.id} value={service.id}>{service.name}</option>
          ))}
        </select>
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <MapPin className="w-4 h-4 inline mr-1" />
          Location
        </label>
        <input
          type="text"
          value={eventForm.location}
          onChange={(e) => setEventForm(prev => ({ ...prev, location: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          placeholder="Event location"
          disabled={readOnly}
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={eventForm.description}
          onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          rows={3}
          disabled={readOnly}
        />
      </div>

      {/* Attendees - Checkbox List */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Users className="w-4 h-4 inline mr-1" />
          Attendees
          {eventForm.attendees.length > 0 && (
            <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
              {eventForm.attendees.length} selected
            </span>
          )}
        </label>
        <div className="border border-gray-300 rounded-lg max-h-[200px] overflow-y-auto">
          {users.length === 0 ? (
            <p className="p-3 text-sm text-gray-500">No users available</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {users.map(u => {
                const isSelected = eventForm.attendees.includes(u.id);
                return (
                  <label
                    key={u.id}
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-red-50' : ''
                    } ${readOnly ? 'cursor-not-allowed opacity-60' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        if (readOnly) return;
                        if (e.target.checked) {
                          setEventForm(prev => ({ 
                            ...prev, 
                            attendees: [...prev.attendees, u.id] 
                          }));
                        } else {
                          setEventForm(prev => ({ 
                            ...prev, 
                            attendees: prev.attendees.filter(id => id !== u.id) 
                          }));
                        }
                      }}
                      disabled={readOnly}
                      className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                      <p className="text-xs text-gray-500 truncate">{u.email}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                      u.role === 'supervisor' ? 'bg-blue-100 text-blue-700' :
                      u.role === 'employee' ? 'bg-green-100 text-green-700' :
                      u.role === 'client' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {u.role}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        {!readOnly && eventForm.attendees.length > 0 && (
          <button
            type="button"
            onClick={() => setEventForm(prev => ({ ...prev, attendees: [] }))}
            className="mt-2 text-xs text-red-600 hover:text-red-700"
          >
            Clear all selections
          </button>
        )}
      </div>

      {/* Reminders */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Bell className="w-4 h-4 inline mr-1" />
          Email Reminders
        </label>
        <div className="space-y-2">
          {eventForm.reminders.map((reminder, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="number"
                value={reminder.time_before}
                onChange={(e) => updateReminder(index, 'time_before', parseInt(e.target.value))}
                className="w-20 px-2 py-1 border border-gray-300 rounded"
                min="1"
                disabled={readOnly}
              />
              <select
                value={reminder.unit}
                onChange={(e) => updateReminder(index, 'unit', e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded"
                disabled={readOnly}
              >
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
                <option value="days">days</option>
              </select>
              <span className="text-sm text-gray-600">before</span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => removeReminder(index)}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          {!readOnly && (
            <button
              type="button"
              onClick={addReminder}
              className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Reminder
            </button>
          )}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
        <textarea
          value={eventForm.notes}
          onChange={(e) => setEventForm(prev => ({ ...prev, notes: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          rows={2}
          disabled={readOnly}
        />
      </div>
    </>
  );
};

export default Calendar;
